import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import DividerMotif from "../components/Brand/DividerMotif";
import Footer from "../components/Layout/Footer";
import { PublicHeader } from "../components/Layout/PublicHeader";
import ScrollReveal from "../components/ui/ScrollReveal";
import { useDocumentMeta } from "../lib/useDocumentMeta";

/*
 * Velaigal /features page.
 *
 * The distinctive move: the page hero renders the word "FIVE" as a massive
 * Fraunces glyph at clamp(160px, 18vw, 280px), low ink opacity, behind the
 * Latin headline. The number IS the visual subject — it counts the five
 * feature blocks the page is about. This mirrors the landing's
 * typography-as-hero treatment with a different glyph (a numeral instead
 * of Tamil).
 *
 * The five feature blocks below use a typographic counter ladder — each
 * "01" "02" etc. in massive Fraunces stroke-only outlined type, sitting
 * left of the headline, rhythmic and editorial.
 *
 * Section transitions use <DividerMotif /> instead of raw borders.
 */

const FEATURES = [
  {
    title: "Tasks generated from your JD",
    description:
      "Describe the role, technical or not, and Velaigal's AI drafts a fixed set of real-world tasks for it. Every applicant gets the same brief, not a moving target.",
  },
  {
    title: "The submission is the application",
    description:
      "A hosted link, a GitHub repo, a document, a video, whatever the task calls for. There is no separate form afterwards: the work a candidate submits, resume attached, is the application.",
  },
  {
    title: "AI pre-screening that flags, never rejects",
    description:
      "Hosted apps get opened and screenshotted in a sandboxed browser. Repos and documents get reviewed against a rubric candidates never see. Everything is scored and flagged, nothing is auto-rejected.",
  },
  {
    title: "Resume-backed applicant profiles",
    description:
      "Every submission arrives with the candidate's resume attached, so recruiters see the claim and the evidence side by side, not one standing in for the other.",
  },
  {
    title: "Recruiter reviews, recruiter decides",
    description:
      "Scored submissions and pre-screening notes land in your applicant list. A person reads the actual work before anyone is shortlisted or passed on.",
  },
];

const USE_CASES = [
  {
    title: "High-volume hiring",
    description:
      "IT services, retail, BPO, healthcare. Roles where consistency matters more than novelty.",
  },
  {
    title: "Campus recruitment",
    description:
      "Pre-screen 5,000 applicants in a week without losing the ones worth talking to.",
  },
  {
    title: "Diversity-led hiring",
    description:
      "Score reasoning and fit, not pedigree or polish. The pipeline gets wider on its own.",
  },
  {
    title: "Niche technical roles",
    description:
      "Rare specialisations still get a task built around what the role actually needs. Generalist roles stay generalist.",
  },
];

const Features: React.FC = () => {
  useDocumentMeta(
    "Features · Velaigal",
    "How Velaigal turns a job description into real tasks, real submissions, and an AI pre-screen that flags instead of rejecting, for technical and non-technical roles alike."
  );

  return (
    <div className="min-h-screen w-full bg-cream text-ink">
      <PublicHeader />

      {/* ---------------- Hero ---------------- *
       * The word "FIVE" in massive Fraunces sits low-opacity behind the
       * headline. The number is the subject; the headline composes in
       * front of it.
       */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-end pointer-events-none select-none"
          style={{ paddingRight: "4vw" }}
        >
          <span
            className="font-display"
            style={{
              fontSize: "clamp(160px, 18vw, 280px)",
              fontWeight: 500,
              lineHeight: 0.85,
              letterSpacing: "-0.05em",
              color: "var(--color-ink)",
              opacity: 0.07,
              fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0',
              animation:
                "velaigalInkClimb 1400ms var(--ease-out-expo) 200ms backwards",
              ["--velaigal-final-opacity" as string]: 0.07,
            }}
          >
            FIVE
          </span>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-8 pt-24 pb-20">
          <span
            className="velaigal-reveal label-editorial block"
            style={{ animationDelay: "250ms" }}
          >
            Features · The whole product, in five
          </span>

          <h1
            className="font-display text-5xl md:text-6xl lg:text-[80px] font-medium text-ink leading-[1.04] tracking-tight mt-6 max-w-4xl"
            style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
          >
            <span
              className="velaigal-reveal block"
              style={{ animationDelay: "350ms" }}
            >
              Five things Velaigal
            </span>
            <span
              className="velaigal-reveal block"
              style={{ animationDelay: "480ms" }}
            >
              does <span className="text-saffron">well.</span>
            </span>
          </h1>

          <span
            aria-hidden="true"
            className="velaigal-rule-draw block w-16 h-px bg-saffron mt-8"
          />

          <p
            className="velaigal-reveal mt-8 text-lg lg:text-xl text-ink-secondary max-w-2xl leading-relaxed"
            style={{ animationDelay: "900ms" }}
          >
            No fillers. No checklists pretending to be features. The five
            below are the product, and each one earns its place.
          </p>
        </div>
      </section>

      <DividerMotif className="py-2" />

      {/* ---------------- Five features as a typographic counter ladder ---------------- *
       * Each feature gets a massive outlined Fraunces numeral on the left,
       * a Fraunces title in the middle column, and the description on the
       * right. Hairline rules between rows. Reads like a magazine table
       * of contents.
       */}
      <section>
        <div className="max-w-7xl mx-auto px-8 py-24">
          <div className="divide-y divide-hairline">
            {FEATURES.map((feature, idx) => {
              const num = String(idx + 1).padStart(2, "0");
              return (
                <ScrollReveal
                  key={feature.title}
                  as="article"
                  className="grid grid-cols-1 lg:grid-cols-12 gap-8 py-16 first:pt-0 last:pb-0 items-start"
                >
                  <div className="lg:col-span-3">
                    <span
                      className="font-display block leading-none"
                      style={{
                        fontSize: "clamp(80px, 8vw, 128px)",
                        fontWeight: 400,
                        letterSpacing: "-0.04em",
                        WebkitTextStroke: "1px var(--color-ink)",
                        WebkitTextFillColor: "transparent",
                        fontVariationSettings:
                          '"opsz" 144, "SOFT" 50, "WONK" 0',
                      }}
                    >
                      {num}
                    </span>
                  </div>
                  <h2
                    className="lg:col-span-5 font-display text-2xl lg:text-3xl font-medium text-ink leading-snug velaigal-headline-hover"
                    style={{
                      fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0',
                    }}
                  >
                    {feature.title}
                  </h2>
                  <p className="lg:col-span-4 text-base text-ink-secondary leading-relaxed">
                    {feature.description}
                  </p>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      <DividerMotif className="py-2" />

      {/* ---------------- Use cases ---------------- *
       * Four cells in a 2x2 grid with 1px hairline gutters. Sentence-case
       * editorial, no icons.
       */}
      <section>
        <div className="max-w-7xl mx-auto px-8 py-24">
          <span className="label-editorial">Use cases</span>
          <h2
            className="font-display text-3xl lg:text-4xl font-medium text-ink leading-tight mt-6 max-w-3xl velaigal-headline-hover"
            style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
          >
            Built for the way Indian teams actually hire.
          </h2>
          <span
            aria-hidden="true"
            className="block w-12 h-px bg-saffron mt-6"
          />

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-hairline">
            {USE_CASES.map((useCase, idx) => (
              <ScrollReveal
                key={useCase.title}
                stagger={(((idx % 4) + 1) as 1 | 2 | 3 | 4)}
                className="bg-cream p-10 md:p-12"
              >
                <span
                  className="label-editorial"
                  style={{ color: "var(--color-saffron)" }}
                >
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <h3
                  className="font-display text-2xl font-medium text-ink leading-snug mt-4 velaigal-headline-hover"
                  style={{
                    fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0',
                  }}
                >
                  {useCase.title}
                </h3>
                <p className="mt-4 text-base text-ink-secondary leading-relaxed max-w-md">
                  {useCase.description}
                </p>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <DividerMotif className="py-2" />

      {/* ---------------- Closing CTA ---------------- */}
      <section>
        <div className="max-w-7xl mx-auto px-8 py-24 text-center">
          <span className="label-editorial">Try it</span>
          <h2
            className="font-display text-4xl lg:text-5xl font-medium text-ink leading-tight tracking-tight max-w-3xl mx-auto mt-6 velaigal-headline-hover"
            style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
          >
            See it on a real role.
          </h2>
          <span
            aria-hidden="true"
            className="block w-12 h-px bg-saffron mx-auto mt-6"
          />
          <p className="mt-6 text-base text-ink-secondary max-w-xl mx-auto">
            Spin up a Velaigal account, upload a JD, and watch the tasks
            write themselves.
          </p>
          <div className="mt-10">
            <Link
              to="/signup/hr"
              className="btn-primary no-underline"
              style={{ textDecoration: "none" }}
            >
              Start hiring
              <ArrowRight className="h-4 w-4 ml-1" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Features;
